import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { FlexCrate, Text, Alert } from '@perch-labs/shrike';
import { copyFor, routes } from 'config';
import { useAccountContext } from 'contexts/account';
import { StandardRow } from 'components/standard-row';
import { LoadingModule } from 'components/loading';
import { ErrorModule } from 'components/errors';
import { trackClickEvent } from 'domain/tracking';
import { StyledCrate } from '../../payment-methods.style';
import {
  getPaymentCopy,
  getPaymentMethodError,
} from '../../payment-methods.utils';
import {
  GET_ACCOUNT_PAYMENT_METHODS,
  GET_PLANS_FOR_ADD_MODAL,
} from '../../payment-methods.api';
import { AddPaymentMethodModal } from '../add-payment-method-modal';
import { StyledButton } from './account-methods.style';

const getCopy = copyFor('settings.paymentInformation');

export const AccountMethods = () => {
  const { selectedAccountId } = useAccountContext();
  const [showAddModal, setShowAddModal] = useState(false);
  const { data, error, loading } = useQuery<
    GetAccountPaymentMethodsQuery,
    GetAccountPaymentMethodsQueryVariables
  >(GET_ACCOUNT_PAYMENT_METHODS, {
    variables: { accountId: selectedAccountId },
  });
  const addModalPlans = useQuery<
    GetPlansForAddModalQuery,
    GetPlansForAddModalQueryVariables
  >(GET_PLANS_FOR_ADD_MODAL, {
    variables: { accountId: selectedAccountId },
  });

  if (loading || addModalPlans.loading)
    return (
      <LoadingModule variant="list" data-testid="account-methods-loading" />
    );
  if (error || !data || addModalPlans.error || !addModalPlans.data)
    return <ErrorModule border={false} />;

  const { paymentMethods } = data.account;
  const { count, plans } = addModalPlans.data.search.plans;

  return (
    <>
      <StyledCrate>
        <Text textStyle="heading700" margin={{ top: '32px' }} tag="h2">
          {getCopy('accountMethods.title')}
        </Text>
        <Text color="secondary" margin={{ top: '16px', bottom: '8px' }}>
          {getCopy('accountMethods.subtitle')}
        </Text>
      </StyledCrate>
      <StyledCrate paddingRightMobile="0px">
        {paymentMethods.map((paymentMethod, index) => {
          const { id, description } = paymentMethod;
          const hasBottomBorder = index === paymentMethods.length - 1;
          const { label, text } = getPaymentCopy({ paymentMethod });
          const paymentMethodPage = routes({
            paymentMethodId: id,
          }).paymentMethod;
          const paymentMethodError = getPaymentMethodError({ paymentMethod });

          return (
            <StandardRow
              iconLinkProps={{
                to: paymentMethodPage,
                ariaLabel: getCopy('accountMethods.ariaLabels.edit', {
                  description,
                }),
              }}
              key={id}
              hasBottomBorder={hasBottomBorder}
              hideTopBorder={index === 0}
            >
              <FlexCrate flexDirection="column">
                {label}
                {text}
                {!!paymentMethodError && (
                  <Alert margin={{ left: '32px', top: '8px' }}>
                    <Text textStyle="paragraph400" color="error">
                      {paymentMethodError}
                    </Text>
                  </Alert>
                )}
              </FlexCrate>
            </StandardRow>
          );
        })}
      </StyledCrate>
      <StyledCrate margin={{ bottom: '32px' }}>
        <StyledButton
          onClick={() => {
            setShowAddModal(!showAddModal);
            trackClickEvent(
              'paymentInformation.accountMethods.addPaymentMethod'
            );
          }}
        >
          {getCopy('paymentMethods.addPaymentMethod')}
        </StyledButton>
      </StyledCrate>
      {showAddModal && (
        <AddPaymentMethodModal
          close={() => setShowAddModal(false)}
          existingPaymentMethodIds={paymentMethods.map(method => method.id)}
          activePlans={plans}
          activePlanCount={count}
        />
      )}
    </>
  );
};
